import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Check, Chrome, Laptop } from 'lucide-react';
import { Button } from '@lightning/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useHowieDojoWebSocket } from '@/hooks/useHowieDojoWebSocket';
import { supabase } from '@/services/supabase';
import { getHowiePasswordResetRedirectUrl } from '@/config/authRedirects';
import {
  EXPERIENCE_LEVEL_DISPLAY,
  type CoachingVoice,
  type DojoExperienceLevel,
  type DojoProfile,
} from '@/types/dojo';
import { cn } from '@/lib/utils';
import { MAC_MIN_OS, macArchLabel } from '@/lib/companionDownload';
import { useDesktopReleases } from '@/lib/useDesktopReleases';

type SettingsSection = 'general' | 'preferences' | 'account' | 'extension';
type CommitmentCadence = NonNullable<DojoProfile['commitment_cadence']>;

const SECTIONS: Array<{ id: SettingsSection; label: string }> = [
  { id: 'general', label: 'Profile' },
  { id: 'preferences', label: 'Preferences' },
  { id: 'account', label: 'Account' },
  { id: 'extension', label: 'Extensions' },
];

/** The HOWIE Companion listing on the Chrome Web Store (the extension id is pinned, so the link is stable); an env var can override it. */
const CHROME_WEB_STORE_URL = 'https://chromewebstore.google.com/detail/howie-companion/gnmhalcilnfmgaihmkapejloojgjbhij';
const extensionUrl = ((import.meta.env.VITE_CHROME_EXTENSION_URL as string | undefined) || CHROME_WEB_STORE_URL).trim();

const LEVELS: DojoExperienceLevel[] = ['new_manager', 'mid_level', 'senior_executive'];

const CADENCES: Array<{ value: CommitmentCadence; label: string; description: string }> = [
  { value: 'ad_hoc', label: 'At my own pace', description: 'Practice whenever the moment is right.' },
  { value: 'weekly', label: 'Weekly rhythm', description: 'Return to one considered practice each week.' },
  { value: '1_per_day', label: 'Daily practice', description: 'Build the muscle through a daily rhythm.' },
];

const VOICES: Array<{ value: CoachingVoice; label: string; description: string }> = [
  { value: 'socratic', label: 'Socratic', description: 'Howie leads with questions and helps you find the pattern.' },
  { value: 'advisory', label: 'Advisory', description: 'Howie offers a clearer point of view when it may help.' },
  { value: 'pattern_interrupt', label: 'Pattern interrupt', description: 'Howie challenges familiar assumptions more directly.' },
];

function ChoiceButton({
  active,
  label,
  description,
  disabled,
  onClick,
}: {
  active: boolean;
  label: string;
  description: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'w-full rounded-xl border px-4 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-accent-warm/35 disabled:cursor-not-allowed disabled:opacity-60',
        active
          ? 'border-how-accent-warm/70 bg-how-accent-warm/[0.12] ring-1 ring-how-accent-warm/15'
          : 'border-how-space-deep/[0.06] bg-white/75 hover:border-how-space-deep/15 hover:bg-white'
      )}
    >
      <span className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-how-space-deep">{label}</span>
        {active && (
          <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-how-accent-warm text-white">
            <Check size={12} strokeWidth={2.5} aria-hidden="true" />
          </span>
        )}
      </span>
      <span className="mt-1 block text-xs leading-5 text-how-text-secondary/85">{description}</span>
    </button>
  );
}

export default function SettingsPage() {
  const { section } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, setExperienceLevel, isSending } = useHowieDojoWebSocket({ userId: user?.id });
  const activeSection: SettingsSection = SECTIONS.some((item) => item.id === section)
    ? (section as SettingsSection)
    : 'general';

  const [displayName, setDisplayName] = useState('');
  const [savedDisplayName, setSavedDisplayName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [savingPreference, setSavingPreference] = useState<string | null>(null);
  const [cadence, setCadence] = useState<DojoProfile['commitment_cadence']>(null);
  const [voice, setVoice] = useState<DojoProfile['preferred_voice']>(null);
  const { releases: desktopReleases, status: desktopReleasesStatus } = useDesktopReleases(activeSection === 'extension');
  const macDownloadUrl = desktopReleases.mac.url;

  useEffect(() => {
    const name = user?.user_metadata?.full_name || user?.user_metadata?.name || '';
    setDisplayName(name);
    setSavedDisplayName(name);
  }, [user]);

  useEffect(() => {
    setCadence(profile?.commitment_cadence ?? null);
    setVoice(profile?.preferred_voice ?? null);
  }, [profile?.commitment_cadence, profile?.preferred_voice]);

  const saveName = async () => {
    const nextName = displayName.trim();
    if (!nextName || !user) return;
    setSavingName(true);
    const { error } = await supabase.auth.updateUser({
      data: { ...user.user_metadata, full_name: nextName, name: nextName },
    });
    setSavingName(false);
    if (error) {
      toast.error(error.message || 'Your name could not be saved.');
      return;
    }
    setSavedDisplayName(nextName);
    toast.success('Name updated');
  };

  const savePreference = async (
    field: 'commitment_cadence' | 'preferred_voice',
    value: CommitmentCadence | CoachingVoice
  ) => {
    if (!user) return;
    setSavingPreference(field);
    const { data, error } = await supabase
      .from('howie_dojo_profiles')
      .update({ [field]: value })
      .eq('user_id', user.id)
      .select('commitment_cadence, preferred_voice')
      .single();
    setSavingPreference(null);

    if (error) {
      toast.error('That preference could not be saved.');
      return;
    }

    setCadence(data.commitment_cadence ?? null);
    setVoice(data.preferred_voice ?? null);
    toast.success('Preference saved');
  };

  const sendPasswordReset = async () => {
    if (!user?.email) return;
    const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: getHowiePasswordResetRedirectUrl(),
    });
    if (error) {
      toast.error(error.message || 'The reset email could not be sent.');
      return;
    }
    toast.success('Password reset email sent');
  };

  return (
    <div
      className="dojo-scroll absolute inset-0 overflow-y-auto px-4 py-10 text-how-space-deep sm:px-8 sm:py-12 lg:py-14"
      style={{ background: 'linear-gradient(180deg, var(--how-paper-soft) 0%, var(--color-how-page-wash) 100%)' }}
    >
      <div className="mx-auto w-full max-w-4xl">
        <header className="text-center">
          <h1 className="font-body text-[30px] font-medium leading-9 tracking-[-0.03em] text-how-space-deep sm:text-4xl sm:leading-tight">
            Settings
          </h1>
          <nav aria-label="Settings" className="mt-7 flex flex-wrap justify-center gap-x-1 gap-y-2 sm:gap-x-2">
            {SECTIONS.map((item) => (
              <button
                key={item.id}
                type="button"
                aria-current={activeSection === item.id ? 'page' : undefined}
                onClick={() => navigate(`/settings/${item.id}`)}
                className={cn(
                  'min-h-11 shrink-0 border-b-2 px-1.5 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-accent-warm/35 sm:px-3 sm:text-sm',
                  activeSection === item.id
                    ? 'border-how-accent-warm text-how-space-deep'
                    : 'border-transparent text-how-text-secondary/85 hover:text-how-space-deep'
                )}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </header>

        <main className="mx-auto mt-10 w-full max-w-3xl">
            {activeSection === 'general' && (
              <div className="space-y-8">
                <section>
                  <h2 className="font-body text-base font-medium text-how-space-deep">Name</h2>
                  <p className="mt-1 text-xs leading-5 text-how-text-secondary/85">How your name appears in Howie.</p>
                  <div className="mt-4 rounded-2xl border border-how-space-deep/[0.06] bg-white/75 p-5">
                    <label className="sr-only" htmlFor="display-name">
                      Name
                    </label>
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <input
                        id="display-name"
                        value={displayName}
                        onChange={(event) => setDisplayName(event.target.value)}
                        className="h-11 min-h-[44px] min-w-0 flex-none rounded-lg border border-how-space-deep/10 bg-white px-3 text-sm text-how-space-deep outline-none transition-colors focus:border-how-space-mid focus:ring-2 focus:ring-how-space-deep/10 sm:min-h-0 sm:flex-1"
                      />
                      <button
                        type="button"
                        onClick={() => void saveName()}
                        disabled={savingName || !displayName.trim() || displayName.trim() === savedDisplayName.trim()}
                        className="h-11 rounded-lg bg-how-space-deep px-5 text-sm font-medium text-white transition-colors hover:bg-how-space-mid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-accent-warm/45 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {savingName ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="font-body text-base font-medium text-how-space-deep">Practice level</h2>
                  <p className="mt-1 text-xs leading-5 text-how-text-secondary/85">
                    Sets the altitude and complexity of the situations you practice.
                  </p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    {LEVELS.map((level) => (
                      <ChoiceButton
                        key={level}
                        active={profile?.experience_level === level}
                        label={EXPERIENCE_LEVEL_DISPLAY[level]}
                        description={
                          level === 'new_manager'
                            ? 'Leading people is new.'
                            : level === 'mid_level'
                              ? 'Teams, targets, and a boss.'
                              : 'Organization-wide decisions.'
                        }
                        disabled={isSending}
                        onClick={() => void setExperienceLevel(level)}
                      />
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeSection === 'preferences' && (
              <div className="space-y-8">
                <section>
                  <h2 className="font-body text-base font-medium text-how-space-deep">Practice rhythm</h2>
                  <p className="mt-1 text-xs leading-5 text-how-text-secondary/85">Choose the cadence you want to build toward.</p>
                  <div className="mt-4 space-y-2.5">
                    {CADENCES.map((option) => (
                      <ChoiceButton
                        key={option.value}
                        active={cadence === option.value}
                        label={option.label}
                        description={option.description}
                        disabled={savingPreference === 'commitment_cadence'}
                        onClick={() => void savePreference('commitment_cadence', option.value)}
                      />
                    ))}
                  </div>
                </section>

                <section>
                  <h2 className="font-body text-base font-medium text-how-space-deep">Coaching voice</h2>
                  <p className="mt-1 text-xs leading-5 text-how-text-secondary/85">Choose how directly Howie should meet you.</p>
                  <div className="mt-4 space-y-2.5">
                    {VOICES.map((option) => (
                      <ChoiceButton
                        key={option.value}
                        active={voice === option.value}
                        label={option.label}
                        description={option.description}
                        disabled={savingPreference === 'preferred_voice'}
                        onClick={() => void savePreference('preferred_voice', option.value)}
                      />
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeSection === 'account' && (
              <div className="space-y-8">
                <section>
                  <div className="divide-y divide-how-space-deep/[0.06] rounded-2xl border border-how-space-deep/[0.06] bg-white/75 px-5">
                    <div className="py-4">
                      <p className="text-sm font-medium text-how-space-deep">Email</p>
                      <p className="mt-1 text-xs leading-5 text-how-text-secondary/85">
                        {user?.email || 'No email available'}
                      </p>
                    </div>
                    <div className="flex flex-col items-start justify-between gap-3 py-4 sm:flex-row sm:items-center">
                      <div>
                        <p className="text-sm font-medium text-how-space-deep">Password</p>
                        <p className="mt-1 text-xs leading-5 text-how-text-secondary/85">Receive a secure link to choose a new password.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => void sendPasswordReset()}
                        className="min-h-11 shrink-0 rounded-lg border border-how-space-deep/10 bg-white px-4 text-xs font-medium text-how-space-deep transition-colors hover:border-how-space-deep/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-accent-warm/35"
                      >
                        Reset
                      </button>
                    </div>
                  </div>
                </section>
              </div>
            )}

            {activeSection === 'extension' && (
              <section className="rounded-3xl border border-how-space-deep/[0.06] bg-white/75 px-6 py-9 text-center shadow-card sm:px-10 sm:py-11">
                <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-how-rose/70 text-how-space-deep">
                  <Chrome size={21} aria-hidden="true" strokeWidth={1.7} />
                </span>

                <h2 className="mt-6 flex flex-wrap items-center justify-center gap-x-2 gap-y-2 font-body text-2xl font-medium leading-tight tracking-[-0.025em] text-how-space-deep sm:text-[1.7rem]">
                  <img src="/logo_green.svg" alt="Howie" className="h-7 w-auto shrink-0" />
                  <span>for Chrome</span>
                </h2>
                <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-how-text-secondary/85">
                  Bring Howie into your browser. Ask about the page you’re reading, a message you’re writing, or a decision on your mind.
                </p>

                <div className="mt-7">
                  {extensionUrl ? (
                    <Button
                      asChild
                      size="lg"
                      className="h-11 rounded-full bg-how-space-deep px-6 font-body text-sm font-normal text-white shadow-none hover:bg-how-space-mid"
                    >
                      <a href={extensionUrl} target="_blank" rel="noreferrer">Add to Chrome</a>
                    </Button>
                  ) : (
                    <span className="inline-flex h-11 items-center rounded-full border border-how-space-deep/10 bg-white/70 px-5 text-xs font-medium text-how-text-secondary/85">
                      Coming soon
                    </span>
                  )}
                </div>

                <div className="mx-auto mt-9 max-w-lg border-t border-how-space-deep/[0.06] pt-6 text-left">
                  <h3 className="font-body text-sm font-medium text-how-space-deep">Designed around your permission</h3>
                  <p className="mt-2 text-xs leading-5 text-how-text-secondary">
                    When you ask, the companion can use context from the page you are viewing to offer guidance. It does not capture screenshots, and you remain in control of what you share.
                  </p>
                </div>
              </section>
            )}

            {activeSection === 'extension' && (
              <section className="mt-6 rounded-3xl border border-how-space-deep/[0.06] bg-white/75 px-6 py-9 text-center shadow-card sm:px-10 sm:py-11">
                <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-how-rose/70 text-how-space-deep">
                  <Laptop size={21} aria-hidden="true" strokeWidth={1.7} />
                </span>

                <h2 className="mt-6 flex flex-wrap items-center justify-center gap-x-2 gap-y-2 font-body text-2xl font-medium leading-tight tracking-[-0.025em] text-how-space-deep sm:text-[1.7rem]">
                  <img src="/logo_green.svg" alt="Howie" className="h-7 w-auto shrink-0" />
                  <span>for Mac</span>
                </h2>
                <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-how-text-secondary/85">
                  Bring Howie into your meetings in Zoom, Microsoft Teams, Slack, and Google Meet. Let it listen when you choose, then review what happened, what you committed to, and how you lived the HOW.
                </p>

                <div className="mt-7">
                  <Button
                    asChild={desktopReleasesStatus !== 'loading'}
                    disabled={desktopReleasesStatus === 'loading'}
                    size="lg"
                    className="h-11 rounded-full bg-how-space-deep px-6 font-body text-sm font-normal text-white shadow-none hover:bg-how-space-mid"
                  >
                    {desktopReleasesStatus === 'loading' ? (
                      'Checking for the latest version…'
                    ) : macDownloadUrl ? (
                      <a href={macDownloadUrl}>Download for Mac</a>
                    ) : (
                      // Manifest unreadable or no build yet: the download page explains either case.
                      <Link to="/companion/download">See download options</Link>
                    )}
                  </Button>
                  <p className="mt-3 text-xs leading-5 text-how-text-secondary">
                    {MAC_MIN_OS}
                    {macDownloadUrl ? ` · ${macArchLabel(desktopReleases.mac.arch)}` : ''}
                  </p>
                  {desktopReleases.mac.intelUrl && (
                    <p className="mt-2 text-xs leading-5 text-how-text-secondary">
                      On an Intel Mac?{' '}
                      <a href={desktopReleases.mac.intelUrl} className="underline underline-offset-4">Download the Intel version</a>.
                    </p>
                  )}
                </div>
                <div className="mx-auto mt-9 max-w-lg border-t border-how-space-deep/[0.06] pt-6 text-left">
                  <h3 className="font-body text-sm font-medium text-how-space-deep">You choose when it listens</h3>
                  <p className="mt-2 text-xs leading-5 text-how-text-secondary">
                    Howie asks for permissions as it needs them. Let others in the meeting know you’re using it, and stop listening at any time.
                  </p>
                  <Link to="/companion/privacy" className="mt-3 inline-block text-xs text-how-space-mid underline underline-offset-4">
                    Privacy and permissions
                  </Link>
                </div>
              </section>
            )}
        </main>
      </div>
    </div>
  );
}
